import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";
import { useTourMode } from "../../../../contexts/TourModeContext";

export interface CompanyProfile {
  symbol: string;
  companyName: string;
  currency: string;
  cik: string;
  isin: string;
  cusip: string;
  exchangeFullName: string;
  exchange: string;
  industry: string;
  website: string;
  description: string;
  ceo: string;
  sector: string;
  country: string;
  fullTimeEmployees: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  image: string;
  ipoDate: string;
  defaultImage: boolean;
  isEtf: boolean;
  isActivelyTrading: boolean;
  isAdr: boolean;
  isFund: boolean;
  price: number;
  beta: number;
  lastDividend: number;
  range: string;
  change: number;
  changePercentage: number;
  volume: number;
  averageVolume: number;
  marketCap: number;
}

interface CompanyProfilesMap {
  [key: string]: CompanyProfile;
}

export function useCompanyProfile(ticker: string) {
  const { isTourMode, getMockData } = useTourMode();

  return useQuery<CompanyProfile[] | CompanyProfile, Error>({
    queryKey: ["companyProfile", ticker, isTourMode],
    queryFn: async () => {
      // In tour mode, return mock data for the ticker
      if (isTourMode) {
        const profiles = getMockData<CompanyProfilesMap>("companyProfiles");
        if (profiles?.[ticker]) {
          return profiles[ticker];
        }
        // Return empty array if no mock data for this ticker
        return [];
      }

      const { data } = await api.get<CompanyProfile[] | CompanyProfile>("/company-profile", {
        params: { ticker },
      });
      return data;
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: isTourMode ? Infinity : 60 * 60 * 1000, // Never refetch in tour mode
  });
}
